import {Page} from "@/components/pages";
import {
    FormHelperText,
    IconButton,
    styled,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
} from "@mui/material";
import {defineMessage, defineMessages, FormattedMessage} from "react-intl";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    FormActionButtons,
    FormattedFormRow,
    FormContainer,
    GenericTable,
    MultipleLineError,
} from "@dvrt/graphql-widgets";
import {useBoolState, useConcertoGenericQuery} from "@/hooks";
import {ContextFilters, useAuth} from "@/context/AuthProvider";
import Loading from "@/components/breadcrumbs/Loading";
import {useStyles} from "@/components/call-group/members/styles";
import {FormattedAlert} from "@dvrt/graphql-widgets";
import FormattedButton from "@/components/FormattedButton";
import {useForm} from "@/utils/Form";
import {useUpdateDomainE164RangesMutation} from "./mutations.generated";
import {createInitialData} from "./initialData";
import {useEffect, useMemo} from "react";
import {DomainE164RangesFragment, DomainE164RangesFragmentDoc} from "./queries.generated";
import {GenericQueryResult} from "@dvrt/graphql-widgets";
import {useDomainNameQuery} from "@/queries.generated";
import ErrorHelper from "@/utils/ErrorHelper";
import {E164Range} from "@/api";

type Props = {
    domainId: number;
};

const FormContent = ({
    domainId,
    rawRanges,
    refetch,
}: {
    domainId: number;
    rawRanges: string;
    refetch: (...args: any) => any;
}) => {
    const form = useForm(
        useUpdateDomainE164RangesMutation,
        "updateDomainE164Ranges",
        createInitialData(domainId, rawRanges),
    );
    const errorHelper = new ErrorHelper(form.errors);

    const [enabled, enable, disable] = useBoolState(true);
    const {classes} = useStyles();

    useEffect(() => {
        form.newInitialData(createInitialData(domainId, rawRanges));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domainId, rawRanges]);

    const onRemoveClick = (index: number) => {
        const currentRanges = structuredClone(form.values.ranges);
        currentRanges.splice(index, 1);
        form.updateValues({ranges: currentRanges});
    };

    const onAddClick = () => {
        const currentRanges = structuredClone(form.values.ranges);
        currentRanges.push({start: "", end: ""});
        form.updateValues({ranges: currentRanges});
    };

    const onChange = (index: number, position: "start" | "end", value: string) => {
        const currentRanges = structuredClone(form.values.ranges);
        currentRanges[index][position] = value;
        form.updateValues({ranges: currentRanges});
    };

    async function submit() {
        disable();

        if (await form.submit()) {
            await refetch();
        }

        enable();
    }

    return (
        <Page
            title={defineMessage({id: "allocatedE164Ranges.title", defaultMessage: "Allocated E164 ranges"})}
            dividerMargin={"none"}
        >
            <FormContainer
                sx={{
                    gridTemplateColumns: "2fr 1fr",
                }}
            >
                {form.values.ranges.length ? (
                    <Table
                        size={"small"}
                        sx={(theme) => ({
                            marginTop: theme.spacing(2),
                        })}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <FormattedMessage
                                        id="allocatedE164Ranges.table.startOfNumberRange"
                                        defaultMessage="Range start"
                                    />
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage
                                        id="allocatedE164Ranges.table.endOfNumberRange"
                                        defaultMessage="Range end"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Tooltip
                                        arrow={true}
                                        placement={"top"}
                                        title={
                                            <FormattedMessage
                                                id="allocatedE164Ranges.buttons.tooltips.add"
                                                defaultMessage="Add new range"
                                            />
                                        }
                                    >
                                        <span>
                                            <IconButton
                                                color="primary"
                                                size="small"
                                                onClick={onAddClick}
                                                disabled={!enabled}
                                            >
                                                <FontAwesomeIcon icon="plus-square" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {form.values.ranges.map((range, idx) => (
                                <InputRow
                                    key={idx}
                                    range={range}
                                    index={idx}
                                    onChange={onChange}
                                    onRemove={onRemoveClick}
                                    disabled={!enabled}
                                    errorHelper={errorHelper}
                                />
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <div className={classes.noMembersContainer}>
                        <FormattedAlert
                            message={defineMessage({
                                id: "allocatedE164Ranges.noAllocatedRanges",
                                defaultMessage: 'There are no allocated ranges! Hit the "Add" to add range.',
                            })}
                            severity="info"
                        />
                        <FormattedButton
                            message={defineMessage({id: "allocatedE164Ranges.buttons.new", defaultMessage: "Add"})}
                            disabled={!enabled}
                            onClick={onAddClick}
                            style={{marginLeft: "1em"}}
                        />
                    </div>
                )}
                <FormattedFormRow>
                    <div style={{marginTop: "25px"}}>
                        <FormActionButtons
                            mainAction={submit}
                            secondaryAction={() => form.reset()}
                            enabled={form.hasChanged && enabled}
                        />
                    </div>
                </FormattedFormRow>
            </FormContainer>
        </Page>
    );
};

type TableRow = {
    id: number;
    startOfNumberRange: string;
    endOfNumberRange: string;
};

const TableContent = ({query}: {query: GenericQueryResult<DomainE164RangesFragment>}) => {
    const modifiedQuery = useMemo(() => {
        const item = query.data.items[0];
        const parsedRanges = JSON.parse(item.endpointE164RangesForAdmins);
        const totalCount = parsedRanges.length;
        const items: TableRow[] = parsedRanges.map((range: [string, string]) => ({
            id: item.id,
            startOfNumberRange: range[0],
            endOfNumberRange: range[1],
        }));

        const fieldNames = ["startOfNumberRange", "endOfNumberRange"];

        return {
            ...query,
            data: {
                items,
                totalCount,
            },
            fieldNames: fieldNames as (keyof TableRow)[],
        };
    }, [query]);

    return (
        <GenericTable<TableRow>
            query={modifiedQuery}
            labels={defineMessages({
                id: {id: "unused", defaultMessage: "Unused"},
                startOfNumberRange: {id: "allocatedE164Ranges.table.startOfNumberRange", defaultMessage: "Range start"},
                endOfNumberRange: {id: "allocatedE164Ranges.table.endOfNumberRange", defaultMessage: "Range end"},
            })}
        />
    );
};

const AllocatedE164Ranges = ({domainId}: Props) => {
    const domainNameQuery = useDomainNameQuery({
        variables: {id: domainId},
    });

    const query = useConcertoGenericQuery<DomainE164RangesFragment>("domains", DomainE164RangesFragmentDoc, {
        fetchPolicy: "no-cache",
        extraVariables: {
            id: domainId,
        },
    });
    const auth = useAuth();

    if (query.loading || domainNameQuery.loading) return <Loading />;

    const domainName = domainNameQuery.data.domains.items[0].name;

    if (auth.hasPermission("edit_superuser_domain", ContextFilters.domain(domainName)))
        return (
            <FormContent
                domainId={domainId}
                rawRanges={query.data.items[0].endpointE164RangesForAdmins}
                refetch={query.refetch}
            />
        );

    return <TableContent query={query} />;
};

type InputRowProps = {
    range: E164Range;
    index: number;
    onChange: (index: number, position: "start" | "end", value: string) => void;
    onRemove: (index: number) => void;
    disabled: boolean;
    errorHelper: ErrorHelper;
};

const InputRow = ({range, index, onChange, onRemove, disabled, errorHelper}: InputRowProps) => {
    const startErrors = errorHelper.fieldErrors(`ranges.${index}.start`);
    const endErrors = errorHelper.fieldErrors(`ranges.${index}.end`);
    const bothErrors = errorHelper.fieldErrors(`ranges.${index}`);

    return (
        <>
            <TableRow key={index}>
                <TableCellWithoutBottom>
                    <TextField
                        variant="standard"
                        name="start"
                        value={range.start}
                        onChange={(e) => onChange(index, "start", e.target.value)}
                        disabled={disabled}
                        error={startErrors.length > 0 || bothErrors.length > 0}
                        helperText={<MultipleLineError errors={startErrors} />}
                    />
                </TableCellWithoutBottom>
                <TableCellWithoutBottom>
                    <TextField
                        variant="standard"
                        name="end"
                        value={range.end}
                        onChange={(e) => onChange(index, "end", e.target.value)}
                        disabled={disabled}
                        error={endErrors.length > 0 || bothErrors.length > 0}
                        helperText={<MultipleLineError errors={endErrors} />}
                    />
                </TableCellWithoutBottom>
                <TableCellWithoutBottom style={{minWidth: "100px"}}>
                    <Tooltip
                        arrow={true}
                        placement={"top"}
                        title={
                            <FormattedMessage
                                id="allocatedE164Ranges.buttons.tooltips.delete"
                                defaultMessage="Delete range"
                            />
                        }
                    >
                        <span>
                            <IconButton
                                size={"small"}
                                color={"secondary"}
                                onClick={() => onRemove(index)}
                                disabled={disabled}
                            >
                                <FontAwesomeIcon icon={"trash"} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </TableCellWithoutBottom>
            </TableRow>
            <TableRow>
                <TableCell colSpan={3} sx={{padding: 0}}>
                    <FormHelperText error={true}>
                        <MultipleLineError errors={bothErrors} />
                    </FormHelperText>
                </TableCell>
            </TableRow>
        </>
    );
};

const TableCellWithoutBottom = styled(TableCell)({
    borderBottom: "none",
});

export default AllocatedE164Ranges;
